import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import {
  CalendarProvider,
  ICalendarConnectionConfig,
  ICalendarConnectionTestResult,
  IDiscoveredCalendar,
  IUserCalendarConnection,
} from '../../Interfaces/IUserCalendarConnection';

const KEY = 'calendar-connections';

export const useCalendarConnections = (idUser: number) => {
  const { data, isLoading, refetch } = useQuery<IUserCalendarConnection[]>({
    refetchOnWindowFocus: false,
    enabled: Number.isFinite(idUser) && idUser > 0,
    queryKey: [KEY, idUser],
    queryFn: () =>
      customFetcher({ endpoint: `/calendar/connections/${idUser}` }),
  });
  return {
    connections: data ?? [],
    isLoadingConnections: isLoading,
    refetchConnections: refetch,
  };
};

export const useCreateCalendarConnection = (idUser: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: {
      Provider: CalendarProvider;
      Label: string;
      IsDefault?: boolean;
      Config?: ICalendarConnectionConfig;
    }) =>
      customFetcher({
        endpoint: `/calendar/connections/${idUser}`,
        method: 'POST',
        body,
      }) as Promise<IUserCalendarConnection>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, idUser] });
    },
  });
  return { createConnection: mutateAsync, isCreatingConnection: isPending };
};

export const useUpdateCalendarConnection = (idUser: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      idUserCalendarConnection,
      patch,
    }: {
      idUserCalendarConnection: number;
      patch: Partial<{
        Label: string;
        IsActive: boolean;
        IsDefault: boolean;
        Config: ICalendarConnectionConfig;
      }>;
    }) =>
      customFetcher({
        endpoint: `/calendar/connections/${idUserCalendarConnection}`,
        method: 'PUT',
        body: patch,
      }) as Promise<IUserCalendarConnection>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, idUser] });
    },
  });
  return { updateConnection: mutateAsync, isUpdatingConnection: isPending };
};

export const useDeleteCalendarConnection = (idUser: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (idUserCalendarConnection: number) =>
      customFetcher({
        endpoint: `/calendar/connections/${idUserCalendarConnection}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, idUser] });
    },
  });
  return { deleteConnection: mutateAsync, isDeletingConnection: isPending };
};

export const useDiscoverCalendars = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (input: {
      Provider: CalendarProvider;
      Config: ICalendarConnectionConfig;
    }) =>
      customFetcher({
        endpoint: `/calendar/connections/discover`,
        method: 'POST',
        body: input,
      }) as Promise<{ calendars: IDiscoveredCalendar[] }>,
  });
  return { discoverCalendars: mutateAsync, isDiscovering: isPending };
};

export const useCreateCalendarConnectionsBatch = (idUser: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: {
      Provider: CalendarProvider;
      Config: ICalendarConnectionConfig;
      Calendars: IDiscoveredCalendar[];
    }) =>
      customFetcher({
        endpoint: `/calendar/connections/${idUser}/batch`,
        method: 'POST',
        body,
      }) as Promise<IUserCalendarConnection[]>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, idUser] });
    },
  });
  return { createMany: mutateAsync, isCreatingMany: isPending };
};

/** Holt die Microsoft-Authorization-URL fuer den OAuth-Popup-Flow.
 *  Das Popup wird vom Frontend direkt mit der `authUrl` geoeffnet — der
 *  Umweg ueber `/api/calendar/authorize/...` (302) wird vermieden, weil
 *  er im Vite-Dev-Setup zu leeren Popups fuehrt. */
export const useGetO365AuthUrl = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (idUser: number) =>
      customFetcher({
        endpoint: `/calendar/o365/auth_url/${idUser}`,
      }) as Promise<{
        ok: boolean;
        authUrl?: string;
        redirectUri?: string;
        message?: string;
      }>,
  });
  return { getO365AuthUrl: mutateAsync, isLoadingO365AuthUrl: isPending };
};

/** Listet die Microsoft-365-Kalender, fuer die der User bereits ein
 *  OAuth-Token hinterlegt hat. Wird nach dem erfolgreichen Login-Popup
 *  aufgerufen, damit der User sich seine Kalender aussuchen kann. */
export const useDiscoverO365Calendars = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (idUser: number) =>
      customFetcher({
        endpoint: `/calendar/o365/discover/${idUser}`,
      }) as Promise<{
        ok: boolean;
        calendars: IDiscoveredCalendar[];
        message?: string;
      }>,
  });
  return {
    discoverO365Calendars: mutateAsync,
    isDiscoveringO365: isPending,
  };
};

export const useTestCalendarConnection = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (idUserCalendarConnection: number) =>
      customFetcher({
        endpoint: `/calendar/connections/${idUserCalendarConnection}/test`,
        method: 'POST',
      }) as Promise<ICalendarConnectionTestResult>,
  });
  return { testConnection: mutateAsync, isTestingConnection: isPending };
};
